import React, { useCallback, useMemo } from 'react';

import styled from 'styled-components';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';

import { Translate } from 'react-localize-redux';

// Store
import AppState from '@webapp/store/state';
import {
  assetOwners,
  getOwnershipTotal,
} from '@webapp/store/ownership/selectors';
import {
  RemoveOwnership,
  CreateOwnership,
  UpdateRequestOwnershipField,
} from '@webapp/store/ownership/action-creators';
import { UpdateReleaseStoreError } from '@webapp/store/release/action-creators';
import { UpdateMerchProductError } from '@webapp/store/merch/action-creators';

// Types
import SimpleUser from '@common/types/user/simple-user';
import { ContactInfo } from '@webapp/types/common/contact-info';

// Services
import { Ownership } from '@services/ownership/types/ownership';
import { NewOwnership } from '@services/ownership/types/new-ownership';
import { ValidOwnershipType } from '@services/ownership/types/valid-ownership-type';
import { ValidAssetType } from '@services/ownership/types/valid-asset-types';

// Components
import BoxHeader from '@components/layout/box-header';
import OwnershipBar from '@components/basics/ownership-bar';
import CirclePlaceHolder from '@components/basics/circle-placeholder';
import ErrorMessage from '@webapp/components/basics/error-message/index';
import OwnershipBarItem from '@components/basics/ownership-bar/ownership-bar-item';

// Controllers
import OwnershipListCtrl from './ownership-list';
import UserInviteSearchCtrl from '@webapp/controllers/user/user-invite-search/index';

import { getTrackIdFromPath } from '@webapp/utils/tracks/get-track-id-from-url-path';
import { sendOwnershipAnalytics } from '@webapp/controllers/track/ownership/utils/analytics';
import { useDebouncedCallback } from 'use-debounce';

const formatName = (owner: Ownership | undefined) => {
  if (!owner) {
    return;
  }
  if (!owner.name) {
    return owner.contact || '?';
  }

  return owner.name
    .split(' ')
    .filter((p) => !!p.trim())
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();
};

const asUser = (owner: Ownership): SimpleUser => {
  const name = owner.name || owner.contact;

  return {
    id: owner?.ownerId || owner?.id,
    fullName: name,
    username: name,
    avatar: '',
  };
};

const isValid = (owner: NewOwnership) => {
  return (
    owner.contact &&
    owner.contactType &&
    owner.split >= 0 &&
    owner.split <= 100 &&
    owner.name
  );
};

interface OwnershipCtrlProps {
  id?: string;
  className?: string;
  title: React.ReactNode;
  text?: React.ReactNode;
  releaseId: string;
  trackId: string;
  type: ValidOwnershipType;
  assetType?: ValidAssetType;
  songName: string;
  locked?: boolean;
  lockedPeriod?: boolean;
  validate?: boolean;
  reassignSplitError?: boolean;
  hideReassignSplitErrorMessage?: boolean;
  isRequest?: boolean;
  isGrey?: boolean;
  user?: any;
  removedByUser?: string;
  givePercentUsername?: string;
  customInputLabel?: React.ReactNode;
  ownershipData: Ownership[];
  withAutosave?: boolean;
  withoutGiveMessage?: boolean;
  updateSplit: (
    ownerId: string,
    updatedSplit: number,
    withAutosave: boolean
  ) => void;
  updateSplitRequest: (
    ownerId: string,
    newSplit: number,
    trackId: string,
    withAutosave: boolean
  ) => void;
  removeSplit: (ownershipId: string, withAutosave: boolean) => void;
  updateOrder: (
    oldIndex: number,
    newIndex: number,
    withAutosave: boolean
  ) => void;
  createOwnershipOnSubmit: (newOwnerShip: Ownership) => void;
  setContact?: React.Dispatch<React.SetStateAction<ContactInfo | undefined>>;
  setName?: React.Dispatch<React.SetStateAction<string>>;
  setGiveawayError: React.Dispatch<React.SetStateAction<boolean>>;
  setNewOwner?: React.Dispatch<React.SetStateAction<NewOwnership | undefined>>;
}

interface StoreProps {
  ownership: Ownership[];
  songwriterError?: boolean;
  ownersError?: boolean;
  createOwnership: (newOwner: NewOwnership) => CreateOwnership;
  sendAnalytics: (
    field: string,
    value: string,
    error: string,
    releaseId?: string
  ) => UpdateReleaseStoreError | UpdateMerchProductError;
  updateRequestSplit: (
    ownershipId: string,
    split: number
  ) => UpdateRequestOwnershipField;
}

const mapStateToProps = (state: AppState, props: OwnershipCtrlProps) => {
  let activeTrackId = getTrackIdFromPath(state);

  if (activeTrackId === '') {
    activeTrackId = state.router.location.pathname.split('/')[4];
  }

  return {
    user: state.user,
    ownership: assetOwners(state.ownership, props.trackId, props.type),
    songwriterError:
      getOwnershipTotal(state.ownership, activeTrackId, 'WRITER') !== 100,
    ownersError:
      getOwnershipTotal(state.ownership, activeTrackId, 'OWNER') !== 100,
  };
};

const mapDispatchToProps = (dispatch: Dispatch, props: OwnershipCtrlProps) => ({
  onRemove: (ownershipId: string) =>
    dispatch(new RemoveOwnership(props.trackId, ownershipId)),
  createOwnership: (newOwner: NewOwnership) =>
    dispatch(new CreateOwnership(newOwner)),
  sendAnalytics: (
    field: string,
    value: string,
    error: string,
    releaseId?: string
  ) =>
    props.assetType === 'TRACK'
      ? dispatch(new UpdateReleaseStoreError(error, field, value, releaseId))
      : dispatch(new UpdateMerchProductError(error, field, value)),
  updateRequestSplit: (ownershipId: string, split: number) =>
    dispatch(
      new UpdateRequestOwnershipField(
        props.trackId,
        ownershipId,
        'split',
        split
      )
    ),
});

export const OwnershipCtrl: React.FC<OwnershipCtrlProps & StoreProps> = ({
  validate,
  isRequest,
  user,
  reassignSplitError,
  hideReassignSplitErrorMessage,
  ownershipData,
  ...props
}) => {
  const { trackId, assetType, type } = props;
  const [newOwner, setNewOwner] = React.useState<NewOwnership>();

  const [highestSplit, setHighestSplit] = React.useState<number>(0);
  const [totalSplit, setTotalSplit] = React.useState<number>(0);

  const setNewOwnerFromProps = props.setNewOwner;

  const handleReset = useCallback(() => {
    const remainingSplit = 0;
    setNewOwner({
      name: '',
      contact: '',
      contactType: 'email',
      split: remainingSplit,
      assetId: trackId,
      assetType: assetType ? assetType : 'TRACK',
      ownerType: 'USER',
      ownershipType: type,
    });
  }, [trackId, assetType, type]);

  React.useEffect(() => {
    handleReset();
  }, [trackId, handleReset]);

  React.useEffect(
    () => setNewOwnerFromProps && setNewOwnerFromProps(newOwner),
    [newOwner, setNewOwner, setNewOwnerFromProps]
  );

  React.useEffect(() => {
    if (ownershipData.find((o) => o.contact === newOwner?.contact)) {
      handleReset();
    }
    setHighestSplit(
      props.ownership.reduce(
        (memo, owner) => (owner.split > memo ? owner.split : memo),
        0
      )
    );
    setTotalSplit(
      ownershipData.reduce((memo, owner) => {
        return memo + owner.split * 10 ** 10;
      }, 0) /
        10 ** 10
    );
  }, [ownershipData, newOwner, handleReset, props.ownership]);

  React.useEffect(() => {
    if (newOwner === undefined) {
      const remainingSplit = 0;

      setNewOwner({
        name: '',
        contact: '',
        contactType: 'email',
        split: remainingSplit,
        assetId: trackId,
        assetType: assetType ? assetType : 'TRACK',
        ownerType: 'USER',
        ownershipType: type,
      });
    }
    setNewOwner((prevState) => ({
      ...(prevState as NewOwnership),
      ownershipType: type,
    }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const ownershipPercentage = useMemo((): number => {
    if (props.ownership.length) {
      return props.ownership.reduce((memo, owner) => memo + owner.split, 0);
    }
    return 0;
  }, [props.ownership]);

  const ownershipCurrentValues = useMemo(() => {
    return ownershipData.map(asUser);
  }, [ownershipData]);

  const handleAddMore = () => {
    if (newOwner !== undefined) {
      return;
    }
    handleReset();
  };

  const handleNameEdit = (value: string) => {
    setNewOwner({
      ...newOwner,
      name: value,
    } as NewOwnership);
  };

  const handleNameSelection = (
    value: Partial<SimpleUser>,
    type: 'user' | 'invite'
  ) => {
    const update: Ownership = {
      ...newOwner,
      contact: (value.email || value.telephone) as string,
      contactType: value.email ? 'email' : 'sms',
      name: value.fullName,
      ownerId: type === 'user' ? value.id : undefined,
      inviteId: type === 'invite' ? value.id : undefined,
      id: '',
      split: 0,
      created_at: new Date(),
      updated_at: new Date(),
    } as Ownership;
    setNewOwner(update);
    handleSaveOwner(update);
  };

  const updateRequestNewSplit = (ownershipId: string, newSplit: number) => {
    props.updateSplitRequest(
      ownershipId,
      newSplit,
      props.trackId,
      !!props.withAutosave
    );
  };

  const updateCurrentSplit = useDebouncedCallback(
    (ownershipId: string, newSplit: number) => {
      props.setGiveawayError(false);
      if (props.lockedPeriod) {
        const currentOwnership = props.ownership.find(
          (o) => o.id == ownershipId
        );
        if (
          !(
            currentOwnership?.ownerId !== user.profile?.id &&
            currentOwnership?.split &&
            currentOwnership.split > newSplit
          )
        ) {
          props.updateSplit(ownershipId, newSplit, !!props.withAutosave);
        } else {
          props.setGiveawayError(true);
        }
      }
      if (!props.lockedPeriod) {
        props.updateSplit(ownershipId, newSplit, !!props.withAutosave);
      }
    },
    500
  );

  const handleSaveOwner = (owner: Ownership) => {
    if (!owner || !isValid(owner)) {
      return;
    }

    props.createOwnershipOnSubmit(owner);
  };

  const updateOrder = (oldIndex: number, newIndex: number) => {
    props.updateOrder(oldIndex, newIndex, !!props.withAutosave);
  };

  React.useEffect(() => {
    sendOwnershipAnalytics({
      reassignSplitError,
      type: props.type,
      assetType: props.assetType,
      trackId: props.trackId,
      songwriterError: props.songwriterError,
      releaseId: props.releaseId,
      validate,
      ownersError: props.ownersError,
      sendAnalytics: props.sendAnalytics,
    });
  }, [validate, props.releaseId, reassignSplitError]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Container>
      <BoxHeader title={props.title} text={props.text} noMargin />

      <UserInviteSearchCtrl
        className="mb-10 mt-20"
        label={
          props.customInputLabel ? (
            props.customInputLabel
          ) : props.type === 'OWNER' ? (
            <Translate id="RELEASE.TRACK_INFO.TRACK.OWNERS.INPUT.NEW" />
          ) : (
            <Translate id="RELEASE.TRACK_INFO.TRACK.SONGWRITERS.INPUT.NEW" />
          )
        }
        currentValues={ownershipCurrentValues}
        type={props.type === 'OWNER' ? 'owner' : 'songwriter'}
        role={props.type}
        locked={(!props.lockedPeriod && isRequest) || props.locked}
        disableInvite={!newOwner?.split}
        onChange={handleNameEdit}
        onSelection={(value, type) => {
          handleNameSelection(value, type);
          handleAddMore();
        }}
        metadata={{
          songName: props.songName,
          releaseId: props.releaseId,
          split: newOwner?.split,
        }}
        setContact={props.setContact}
        setName={props.setName}
      />

      <OwnershipListCtrl
        ownership={ownershipData.filter((owner) =>
          isRequest
            ? (owner.ownerId === user.profile?.id &&
                owner.ownershipType === type) ||
              !owner.id
            : owner.ownershipType === type
        )}
        onRemove={
          props.locked
            ? () => null
            : (ownerId) => props.removeSplit(ownerId, !!props.withAutosave)
        }
        updateCurrentSplit={props.locked ? () => null : updateCurrentSplit}
        updateOwnershipOrder={props.locked ? () => null : updateOrder}
        updateRequestNewSplit={!isRequest ? () => null : updateRequestNewSplit}
        disabled={props.locked}
        request={isRequest}
        userId={user.profile?.id}
        isRequest={isRequest}
        removedByUser={props.removedByUser}
        givePercentUsername={props.givePercentUsername}
        leftDecoration={<CirclePlaceHolder />}
        lockedPeriod={props.lockedPeriod}
        withoutGiveMessage={props.withoutGiveMessage}
      />

      {!isRequest && ownershipData.length && ownershipPercentage > 0 ? (
        <>
          <Separator />
          <OwnershipBar className="mt-30">
            {/* TODO: DEVTEAM-831 - We need to change this map so we can merge all of the members except the active user as in the design so we have 2 values, the active user and the others. */}
            {ownershipData
              .filter((owner) => owner.split && owner.split > 0)
              .map((owner) => {
                return (
                  <OwnershipBarItem
                    className="item"
                    key={`ownership-split-bar-${
                      owner.id
                        ? owner.id
                        : `${owner.ownerType}#${owner.ownershipType}#${owner.ownerId}`
                    }`}
                    percentage={owner.split}
                    barWidth={owner.split}
                    // TODO: DEVTEAM-831 - We need to create a new format for the name so we can use it as a group and grouping 4 members as in the design.
                    name={formatName(owner)}
                    isGrey={props.isGrey && user.profile?.id !== owner.ownerId}
                    // TODO: DEVTEAM-831 - isYellow need to be reviewed. This is just for demo.
                    isYellow={
                      !!owner.requestedSplit || !!props.givePercentUsername
                    }
                    // TODO: DEVTEAM-831 - isRed sholud come from each owner. This is just for demo.
                    isRed={!!props.removedByUser || totalSplit > 100}
                    isYou={user.profile?.id === owner.ownerId}
                    barComplete={ownershipPercentage === 100}
                    hasHigherSplit={owner.split === highestSplit}
                  />
                );
              })}
          </OwnershipBar>
        </>
      ) : null}
      {isRequest && ownershipData.length && ownershipPercentage > 0 ? (
        <>
          <Separator />
          <OwnershipBar className="mt-30">
            {ownershipData.find(
              (owner) =>
                owner.split &&
                owner.split > 0 &&
                owner.ownerId === user.profile?.id &&
                owner.ownershipType === type
            ) ? (
              <OwnershipBarItem
                className="item"
                key={`ownership-split-bar-${
                  ownershipData.find(
                    (owner) =>
                      owner.split &&
                      owner.split > 0 &&
                      owner.ownerId === user.profile?.id
                  )?.id
                }`}
                percentage={
                  ownershipData.find(
                    (owner) =>
                      owner.split &&
                      owner.split > 0 &&
                      owner.ownerId === user.profile?.id
                  )?.split
                }
                barWidth={
                  ownershipData.find(
                    (owner) =>
                      owner.split &&
                      owner.split > 0 &&
                      owner.ownerId === user.profile?.id
                  )?.split
                }
                // TODO: DEVTEAM-831 - We need to create a new format for the name so we can use it as a group and grouping 4 members as in the design.
                name={formatName(
                  ownershipData.find(
                    (owner) =>
                      owner.split &&
                      owner.split > 0 &&
                      owner.ownerId === user.profile?.id
                  )
                )}
                isGrey={
                  props.isGrey &&
                  user.profile?.id !==
                    ownershipData.find(
                      (owner) =>
                        owner.split &&
                        owner.split > 0 &&
                        owner.ownerId === user.profile?.id
                    )?.ownerId
                }
                // TODO: DEVTEAM-831 - isYellow need to be reviewed. This is just for demo.
                isYellow={
                  !!ownershipData.find(
                    (owner) =>
                      owner.split &&
                      owner.split > 0 &&
                      owner.ownerId === user.profile?.id
                  )?.requestedSplit || !!props.givePercentUsername
                }
                // TODO: DEVTEAM-831 - isRed sholud come from each owner. This is just for demo.
                isRed={!!props.removedByUser || totalSplit > 100}
                isYou={
                  user.profile?.id ===
                  ownershipData.find(
                    (owner) =>
                      owner.split &&
                      owner.split > 0 &&
                      owner.ownerId === user.profile?.id
                  )?.ownerId
                }
                barComplete={ownershipPercentage === 100}
              />
            ) : (
              <></>
            )}
            {ownershipData.filter(
              (owner) =>
                owner.split &&
                owner.split > 0 &&
                owner.ownerId !== user.profile?.id &&
                owner.ownershipType === type
            ).length ? (
              <OwnershipBarItem
                className="item"
                key={`ownership-split-bar-${
                  ownershipData.filter(
                    (owner) =>
                      owner.split &&
                      owner.split > 0 &&
                      owner.ownerId !== user.profile?.id &&
                      owner.ownershipType === type
                  )[0]?.id
                }`}
                // TODO: DEVTEAM-831 - We need to calculate the newPercentage and remove this percentage from the others.
                // newPercentage={14}
                percentage={ownershipData
                  .filter(
                    (owner) =>
                      owner.split &&
                      owner.split > 0 &&
                      owner.ownerId !== user.profile?.id &&
                      owner.ownershipType === type
                  )
                  .reduce((accum, item) => accum + item.split, 0)}
                barWidth={ownershipData
                  .filter(
                    (owner) =>
                      owner.split &&
                      owner.split > 0 &&
                      owner.ownerId !== user.profile?.id &&
                      owner.ownershipType === type
                  )
                  .reduce((accum, item) => accum + item.split, 0)}
                // TODO: DEVTEAM-831 - We need to create a new format for the name so we can use it as a group and grouping 4 members as in the design.
                name={`${
                  ownershipData.filter(
                    (owner) =>
                      owner.split &&
                      owner.split > 0 &&
                      owner.ownerId !== user.profile?.id &&
                      owner.ownershipType === type
                  ).length
                } Owners`}
                isGrey={false}
                // TODO: DEVTEAM-831 - isYellow need to be reviewed. This is just for demo.
                isYellow={false}
                // TODO: DEVTEAM-831 - isRed sholud come from each owner. This is just for demo.
                isRed={!!props.removedByUser || totalSplit > 100}
                isYou={false}
                barComplete={ownershipPercentage === 100}
              />
            ) : (
              <></>
            )}
          </OwnershipBar>
        </>
      ) : null}
      {reassignSplitError && totalSplit !== 100 ? (
        <ErrorMessage
          className="mt-10"
          hasBackground
          errorId="RELEASE.SPLIT_OWNER_ERROR"
        />
      ) : null}
      {validate && props.type === 'WRITER' && totalSplit <= 100 ? (
        <ErrorMessage
          className="mt-10"
          hasBackground
          errorId={
            props.songwriterError ? 'RELEASE.MISSING_SONGWRITER' : undefined
          }
        />
      ) : null}
      {validate && props.type === 'OWNER' && totalSplit <= 100 ? (
        <ErrorMessage
          className="mt-10"
          hasBackground
          errorId={props.ownersError ? 'RELEASE.MISSING_OWNERS' : undefined}
        />
      ) : null}
    </Container>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(OwnershipCtrl);

const Container = styled.div`
  .percentage-input {
    .input {
      text-align: right;
    }
  }
`;

const Separator = styled.span`
  height: 1px;
  width: calc(100% + 40px);
  border: 1px dashed ${({ theme }) => theme.colors.border};
  border-bottom: 0;
  margin: 20px 0 20px -20px;
  display: block;

  ${({ theme }) => `@media screen and ${theme.device.laptopL}`} {
    width: calc(100% + 60px);
    margin: 30px 0 30px -30px;
  }
`;
